import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { message } from "antd";
import { RecommendationRun, RecommendationRunApi } from "../api/RecommendationRunApi";

type RecommendationRunContextValue = {
  currentRun: RecommendationRun | null;
  isActive: boolean;
  initialLoading: boolean;
  startingRun: boolean;
  cancellingRun: boolean;
  refreshRun: () => Promise<RecommendationRun | null>;
  startRun: () => Promise<RecommendationRun | null>;
  cancelRun: () => Promise<RecommendationRun | null>;
};

const RecommendationRunContext = createContext<RecommendationRunContextValue>({
  currentRun: null,
  isActive: false,
  initialLoading: true,
  startingRun: false,
  cancellingRun: false,
  refreshRun: async () => null,
  startRun: async () => null,
  cancelRun: async () => null,
});

export const RecommendationRunProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [currentRun, setCurrentRun] = useState<RecommendationRun | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [startingRun, setStartingRun] = useState(false);
  const [cancellingRun, setCancellingRun] = useState(false);

  const refreshRun = useCallback(async () => {
    try {
      const run = await RecommendationRunApi.getActiveOrLatest();
      setCurrentRun(run);
      return run;
    } catch (error) {
      console.error("Error refreshing recommendation run", error);
      return null;
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRun();
  }, [refreshRun]);

  useEffect(() => {
    if (!currentRun?.active) {
      return;
    }

    const intervalId = window.setInterval(() => {
      refreshRun();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentRun?.active, refreshRun]);

  const startRun = useCallback(async () => {
    setStartingRun(true);
    try {
      const run = await RecommendationRunApi.startRun();
      setCurrentRun(run);
      message.success("La generación de recomendaciones se inició correctamente.");
      return run;
    } catch (error: any) {
      const status = error?.response?.status;
      const apiError = error?.response?.data?.apierror;
      const backendMessage =
        apiError?.message_ES ??
        apiError?.message ??
        (typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message);
      if (status === 409) {
        message.warning(
          backendMessage || "Ya hay una generación de recomendaciones en curso.",
        );
        await refreshRun();
        return null;
      }
      message.error(
        backendMessage ||
          "No se pudo iniciar la generación de recomendaciones. Faltan configuraciones o ocurrió un error técnico. Por favor comuníquense con nosotros.",
      );
      return null;
    } finally {
      setStartingRun(false);
    }
  }, [refreshRun]);

  const cancelRun = useCallback(async () => {
    if (!currentRun?.id) {
      return null;
    }

    setCancellingRun(true);
    try {
      const run = await RecommendationRunApi.cancelRun(currentRun.id);
      setCurrentRun(run);
      message.success("La generación de recomendaciones se canceló.");
      return run;
    } catch (error: any) {
      const backendMessage =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message;
      message.error(
        backendMessage || "No se pudo cancelar la generación de recomendaciones.",
      );
      await refreshRun();
      return null;
    } finally {
      setCancellingRun(false);
    }
  }, [currentRun?.id, refreshRun]);

  const value = useMemo(
    () => ({
      currentRun,
      isActive: Boolean(currentRun?.active),
      initialLoading,
      startingRun,
      cancellingRun,
      refreshRun,
      startRun,
      cancelRun,
    }),
    [cancelRun, cancellingRun, currentRun, initialLoading, refreshRun, startRun, startingRun],
  );

  return (
    <RecommendationRunContext.Provider value={value}>
      {children}
    </RecommendationRunContext.Provider>
  );
};

export const useRecommendationRun = () => useContext(RecommendationRunContext);
